import Axhelper from "@/lib/Axhelper";

/**
 * CustomCollectionsService - Handles all custom collections-related API operations
 * Separates API logic from React hooks and components
 */
class CustomCollectionsServiceNew {
  constructor() {
    this.baseUrl = window.RUNTIME_CONFIG.API_URL;
    this.endpoints = {
      customCollections: `/api/customcollections/filters`,
      customCollectionsDelete: `/api/customcollections/delete/`,
      customCollectionsCreate: `/api/customcollections/`,
      customCollectionsUpdate: `/api/customcollections/`
    };
  }

  async getDependentCollectionsFor(collectionName) {
    try {
      const trimmedName = (collectionName || '').trim();
      if (!trimmedName) {
        return {
          data: { collections: [] },
          error: 'Collection name is required'
        };
      }

      const url = `/api/customcollections/showcollectionswithlookupto/${encodeURIComponent(trimmedName)}`;
      const response = await Axhelper.sendGetAsync(url);

      if (response.error) {
        return {
          data: { collections: [] },
          error: response.error
        };
      }

      return {
        data: response.data || { collections: [] },
        error: null
      };
    } catch (error) {
      console.error('CustomCollectionsService.getDependentCollectionsFor error:', error);
      return {
        data: { collections: [] },
        error: error.message || 'Failed to load dependent collections'
      };
    }
  }

  async getCustomCollectionById(customCollectionId) {
    try {
      if (!customCollectionId) {
        return {
          data: null,
          error: 'Invalid custom collection ID provided'
        };
      }

      const url = `${this.endpoints.customCollections}?id=${encodeURIComponent(customCollectionId)}`;
      const response = await Axhelper.sendGetAsync(url);

      if (response.error) {
        return {
          data: null,
          error: response.error
        };
      }

      // Assume response.data is an array with one item
      if (Array.isArray(response.data) && response.data.length > 0) {
        return {
          data: response.data[0],
          error: null
        };
      }

      return {
        data: null,
        error: 'Failed to fetch custom collection'
      };
    } catch (error) {
      console.error('CustomCollectionsService.getCustomCollectionById error:', error);
      return {
        data: null,
        error: error.message || 'Failed to fetch custom collection'
      };
    }
  }

  async getCustomCollectionsWithFilters(options = {}) {
    try {
      const {
        limit = 50,
        sortColumn = 'created',
        sortDirection = 'desc',
        filters = {}
      } = options;

      const url = this._buildFilteredUrl({
        limit,
        sortColumn,
        sortDirection,
        filters
      });

      const response = await Axhelper.sendGetAsync(url);

      if (response.error) {
        return {
          data: [],
          error: response.error,
          totalCount: 0,
          hasMore: false
        };
      }

      // Parse pagination info from headers
      const totalCount = response.headers?.['x-total-count']
        ? parseInt(response.headers['x-total-count'], 10)
        : response.data?.length || 0;

      const hasMore = response.data?.length < totalCount;

      return {
        data: response.data || [],
        error: null,
        totalCount,
        hasMore,
        headers: response.headers
      };
    } catch (error) {
      console.error('CustomCollectionsService.getCustomCollectionsWithFilters error:', error);
      return {
        data: [],
        error: error.message || 'Failed to fetch custom collections',
        totalCount: 0,
        hasMore: false
      };
    }
  }

  async deleteCustomCollection(customCollectionId) {
    try {
      if (!customCollectionId) {
        return {
          success: false,
          error: 'Invalid custom collection ID provided',
          message: null
        };
      }

      const url = `${this.endpoints.customCollectionsDelete}${encodeURIComponent(customCollectionId)}`;
      const response = await Axhelper.sendDeleteAsync(url);

      if (response.error) {
        return {
          success: false,
          error: response.error,
          message: null
        };
      }

      return {
        success: true,
        error: null,
        message: response.data || 'Custom collection deleted successfully'
      };
    } catch (error) {
      console.error('CustomCollectionsService.deleteCustomCollection error:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete custom collection',
        message: null
      };
    }
  }

  async createCustomCollection(customCollectionData) {
    try {
      const response = await Axhelper.sendPostAsync(this.endpoints.customCollectionsCreate, customCollectionData);
      if (response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      return {
        success: true,
        error: null,
        data: response.data || null
      };
    } catch (error) {
      console.error('CustomCollectionsService.createCustomCollection error:', error);
      return {
        success: false,
        error: error.message || 'Failed to create custom collection'
      };
    }
  }

  async updateCustomCollection(customCollectionId, customCollectionData) {
    try {
      if (!customCollectionId) {
        return {
          success: false,
          error: 'Invalid custom collection ID provided'
        };
      }

      const url = `${this.endpoints.customCollectionsUpdate}/${encodeURIComponent(customCollectionId)}`;
      const response = await Axhelper.sendPutAsync(url, customCollectionData);

      if (response.error) {
        return {
          success: false,
          error: response.error
        };
      }

      return {
        success: true,
        error: null,
        data: response.data || null
      };
    } catch (error) {
      console.error('CustomCollectionsService.updateCustomCollection error:', error);
      return {
        success: false,
        error: error.message || 'Failed to update custom collection'
      };
    }
  }
  
  _buildFilteredUrl({ limit, sortColumn, sortDirection, filters }) {
    let params = [
      `limit=${limit}`,
      `sortcol=${sortColumn}`,
      `sortdir=${sortDirection}`
    ];

    // Add search text filter
    if (filters.searchText && filters.searchText.length >= 3) {
      params.push(`searchText=${encodeURIComponent(filters.searchText)}`);
    }

    return `${this.endpoints.customCollections}?${params.join('&')}`;
  }

}

// Export a singleton instance
export default new CustomCollectionsServiceNew();